/**
 * access-denied-redirect — handler za 403 odgovore koji nose
 * lifecycle-gate payload od ``app.core.tenants.lifecycle_dependency``.
 *
 * Backend vraća ``HTTPException(status_code=403, detail={"error": "...",
 * "redirect": "/..."})``. FastAPI ovaj objekat upakuje pod ``response.detail``
 * (ne na top-level), pa parsiramo oba moguća oblika:
 *
 *   { "detail": { "error": "tenant_suspended", "redirect": "/suspended" } }
 *   { "error": "tenant_suspended", "redirect": "/suspended" }   // backward-compat
 *
 * Dvije ulazne tačke:
 *
 *   1. ``handleAccessDenied403(response)`` — pozovi nakon ``fetch`` poziva
 *      kad ``response.status === 403``. Ako je payload tenant_*, izvrši
 *      ``router.push(redirect)`` (ako se može, koristeći history API) i
 *      vrati ``true``. Inače ``false``.
 *
 *   2. ``installFetchInterceptor()`` — patchuje ``window.fetch`` da
 *      automatski hvata svaki 403 sa lifecycle payload-om i redirektuje.
 *      Zovi jednom u app shell-u (ErpShell). Idempotent.
 *
 * NIKAD ne refreshujemo stranicu — koristimo ``history.pushState`` da bi
 * Next.js router pokupio novu rutu (Velibor pravilo "pozadinske operacije
 * bez refresh-a"). Ako Next-ov client router nije dostupan (rare edge),
 * fallback na ``window.location.assign``.
 */

// Velibor 2026-05-02 (veče): svi lifecycle 403 razlozi sad redirektuju na
// jednu /tenant-status stranicu sa ?reason= query parametrom (umjesto na 4
// odvojene rute /suspended, /expired, /payment-failed, /deactivated).
// Vidi mockup tenant-status-finetune.html.
const KNOWN_LIFECYCLE_ERRORS = new Set([
  "tenant_suspended",
  "tenant_expired",
  "tenant_payment_failed",
  "tenant_deactivated",
]);

const KNOWN_REDIRECTS: Record<string, string> = {
  tenant_suspended: "/tenant-status?reason=suspended",
  tenant_expired: "/tenant-status?reason=expired",
  tenant_payment_failed: "/tenant-status?reason=payment_failed",
  tenant_deactivated: "/tenant-status?reason=deactivated",
};

interface LifecyclePayload {
  error: string;
  redirect: string;
}

function extractLifecyclePayload(body: unknown): LifecyclePayload | null {
  if (!body || typeof body !== "object") return null;
  const top = body as Record<string, unknown>;

  // FastAPI HTTPException → { detail: {...} }
  const detail = top.detail;
  if (detail && typeof detail === "object") {
    const d = detail as Record<string, unknown>;
    const err = typeof d.error === "string" ? d.error : null;
    const red = typeof d.redirect === "string" ? d.redirect : null;
    if (err && KNOWN_LIFECYCLE_ERRORS.has(err)) {
      // Velibor 2026-05-02: backend može još slati legacy redirect-e
      // (/suspended, /expired itd) — uvijek ih normalizujemo na /tenant-status
      // mapiranje (KNOWN_REDIRECTS). Ako backend već šalje novi /tenant-status
      // URL, koristimo njega.
      const isLegacy =
        red &&
        (red === "/suspended" ||
          red === "/expired" ||
          red === "/payment-failed" ||
          red === "/deactivated");
      const final = !red || isLegacy ? KNOWN_REDIRECTS[err] : red;
      return { error: err, redirect: final };
    }
  }

  // Backward-compat: payload direktno na top-level
  const err = typeof top.error === "string" ? top.error : null;
  const red = typeof top.redirect === "string" ? top.redirect : null;
  if (err && KNOWN_LIFECYCLE_ERRORS.has(err)) {
    const isLegacy =
      red &&
      (red === "/suspended" ||
        red === "/expired" ||
        red === "/payment-failed" ||
        red === "/deactivated");
    const final = !red || isLegacy ? KNOWN_REDIRECTS[err] : red;
    return { error: err, redirect: final };
  }
  return null;
}

function navigate(redirect: string): void {
  if (typeof window === "undefined") return;
  // Već smo na ciljnoj ruti? Ne radi ništa (sprečava petlju ako stranica
  // sama poziva access-check ili druge endpointe). Velibor 2026-05-02:
  // redirect sad može biti "/tenant-status?reason=X" — poredimo pathname
  // + search dio (ne samo pathname).
  const current = window.location.pathname + window.location.search;
  if (current === redirect || window.location.pathname === redirect) return;

  // Probaj Next.js client navigation (history.pushState + popstate listener).
  // Next router automatski pokupi pushState event u isAppRouter. Ako iz
  // nekog razloga ne uhvati, fallback na hard navigation u 60ms.
  try {
    window.history.pushState({}, "", redirect);
    window.dispatchEvent(new PopStateEvent("popstate"));
    setTimeout(() => {
      const now = window.location.pathname + window.location.search;
      if (now !== redirect && window.location.pathname !== redirect) {
        window.location.assign(redirect);
      }
    }, 60);
  } catch {
    window.location.assign(redirect);
  }
}

/**
 * Pozovi nakon fetch-a sa response.status === 403. Ako nosi lifecycle
 * payload, redirektuj i vrati ``true``. Inače vrati ``false``.
 *
 * Kloniramo response prije ``.json()`` da pozivalac može i dalje da čita
 * tijelo nakon ovog handlera (fetch body se može pročitati samo jednom).
 */
export async function handleAccessDenied403(
  response: Response,
): Promise<boolean> {
  if (response.status !== 403) return false;
  let body: unknown;
  try {
    body = await response.clone().json();
  } catch {
    return false;
  }
  const payload = extractLifecyclePayload(body);
  if (!payload) return false;
  navigate(payload.redirect);
  return true;
}

/**
 * Globalni fetch interceptor — patchuje ``window.fetch`` da automatski
 * hvata 403 lifecycle response-ove. Idempotentno: drugi poziv je no-op.
 *
 * Pozovi jednom u ``ErpShell`` (ili ``layout.tsx``) tako da svaki
 * fetch iz aplikacije bude pokriven. Lokalni fetch-evi koji eksplicitno
 * žele drukčije ponašanje mogu da postave header
 * ``X-Skip-Lifecycle-Redirect: 1`` na ``Request`` (interceptor ga gleda).
 */
declare global {
  interface Window {
    __grozd_fetch_lifecycle_intercept__?: boolean;
  }
}

export function installFetchInterceptor(): void {
  if (typeof window === "undefined") return;
  if (window.__grozd_fetch_lifecycle_intercept__) return;
  window.__grozd_fetch_lifecycle_intercept__ = true;

  const original = window.fetch.bind(window);
  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const headers = new Headers(init?.headers || (input as Request).headers);
    const skip = headers.get("X-Skip-Lifecycle-Redirect");
    const response = await original(input as RequestInfo, init);
    if (skip === "1") return response;
    if (response.status === 403) {
      // Best-effort — ako padne, pozivalac dobija original response.
      try {
        await handleAccessDenied403(response);
      } catch {
        // ignore
      }
    }
    return response;
  };
}
